import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { AppAbility } from '@tronche2tek/shared-types';
import { use, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

import { AbilityContext } from '../../ability';
import { usersSearchQueryOptions } from '../../api/users';
import { type User } from '../../types';
import { UserAvatar } from '../photo/UserAvatar';
import { Modal } from './Modal';

type Action = {
  id: string;
  label: string;
  hint: string;
  to: string;
};

type Item = { kind: 'action'; action: Action } | { kind: 'user'; user: User };

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ability = use(AbilityContext);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);

  // Results are keyed by the query: a slow answer to an earlier keystroke can
  // never stand in for the current one, and React Query aborts it.
  const canSearch = ability.can('manage', 'Photo');
  const [debouncedQuery] = useDebounce(query.trim(), 250);
  const search = useQuery({
    ...usersSearchQueryOptions(debouncedQuery),
    enabled: open && canSearch && debouncedQuery.length >= 2,
  });
  // Reopened, the debounced value still holds the last search for 250ms.
  const loading = search.isFetching && query.trim().length > 0;

  const allActions = usePaletteActions(ability);

  // Reset fields as the palette opens (set-during-render, not an effect).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery('');
      setCursor(0);
    }
  }

  const filteredActions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return allActions;
    }
    return allActions.filter(
      (a) =>
        a.label.toLowerCase().includes(q) || a.hint.toLowerCase().includes(q),
    );
  }, [allActions, query]);

  // Hide stale user results once the query is cleared.
  const hasQuery = query.trim().length > 0;
  const users = search.data;
  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    for (const a of filteredActions) {
      out.push({ kind: 'action', action: a });
    }
    if (hasQuery) {
      for (const u of users ?? []) {
        out.push({ kind: 'user', user: u });
      }
    }
    return out;
  }, [filteredActions, users, hasQuery]);

  // Keep the cursor in range as the result list grows or shrinks.
  const [prevLen, setPrevLen] = useState(items.length);
  if (items.length !== prevLen) {
    setPrevLen(items.length);
    setCursor((c) => (items.length === 0 ? 0 : Math.min(c, items.length - 1)));
  }

  const listId = useId();

  function pick(item: Item) {
    onClose();
    if (item.kind === 'action') {
      void navigate({ to: item.action.to });
    } else {
      void navigate({
        to: '/users/$oid',
        params: { oid: item.user.id },
      });
    }
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    // Enter also confirms an IME candidate (Chinese, Japanese, Korean input):
    // that keystroke belongs to the composition, not to this list.
    if (e.nativeEvent.isComposing) {
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (items.length > 0) {
        setCursor((c) => (c + 1) % items.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (items.length > 0) {
        setCursor((c) => (c - 1 + items.length) % items.length);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = items[cursor];
      if (target) {
        pick(target);
      }
    }
  }

  if (!open) {
    return null;
  }

  return (
    <Modal
      aria-label={t('palette.label')}
      onClose={onClose}
      className="backdrop:bg-ink/60"
    >
      <div className="mt-[10vh] w-full max-w-xl self-start border border-ink bg-card text-ink shadow-[6px_6px_0_var(--ink)]">
        <div className="relative border-b-2 border-b-ink">
          <input
            // react-doctor-disable-next-line react-doctor/no-autofocus -- in a modal <dialog>, autofocus is how the opening focus is chosen (the browser's dialog focusing steps)
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            // Focus stays in the field; the arrows move a highlight that the
            // combobox announces through aria-activedescendant.
            role="combobox"
            aria-label={t('palette.label')}
            aria-expanded={items.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              items.length > 0 ? optionId(listId, cursor) : undefined
            }
            type="text"
            placeholder={t('palette.placeholder', 'Action ou utilisateur…')}
            className="w-full bg-transparent px-4 py-3 text-base font-medium caret-blue focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <span className="mono absolute top-1/2 right-3 -translate-y-1/2 border border-rule px-1.5 py-0.5 text-[10px] text-muted">
            {loading ? '…' : 'ESC'}
          </span>
        </div>

        {items.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted">
            {query.trim().length === 0
              ? t('palette.empty', 'Tape pour filtrer.')
              : t('palette.noMatch', 'Aucun résultat.')}
          </p>
        ) : (
          <ul
            id={listId}
            role="listbox"
            aria-label={t('palette.label')}
            className="max-h-[60vh] overflow-y-auto"
          >
            {items.map((item, idx) => {
              const key =
                item.kind === 'action' ? item.action.id : item.user.id;
              return (
                <PaletteOption
                  key={`${item.kind}-${key}`}
                  id={optionId(listId, idx)}
                  item={item}
                  active={idx === cursor}
                  onPick={() => pick(item)}
                  onHover={() => setCursor(idx)}
                />
              );
            })}
          </ul>
        )}

        <div className="mono flex items-center gap-4 border-t border-t-rule px-4 py-2 text-[10px] text-muted">
          <span>↑↓ {t('palette.help.navigate', 'naviguer')}</span>
          <span>↵ {t('palette.help.open', 'ouvrir')}</span>
          <span>ESC {t('palette.help.close', 'fermer')}</span>
        </div>
      </div>
    </Modal>
  );
}

/** The navigation shortcuts, limited to what the signed-in role may open. */
function usePaletteActions(ability: AppAbility): Action[] {
  const { t } = useTranslation();
  const canBulk = ability.can('upload', 'Batch');
  const canModerate = ability.can('moderate', 'Photo');
  const canGenerateCards = ability.can('generate', 'Card');
  const isAdmin = ability.can('manage', 'all');

  return useMemo<Action[]>(() => {
    const list: Action[] = [
      // Was two entries pointing at two pages that are now one.
      {
        id: 'me-profile',
        label: t('palette.actions.profile', 'Mon profil'),
        hint: '/me',
        to: '/me',
      },
    ];
    if (canModerate) {
      list.push({
        id: 'moderation',
        label: t('palette.actions.moderation', 'Modération'),
        hint: '/photos/moderation',
        to: '/photos/moderation',
      });
    }
    if (canBulk) {
      list.push({
        id: 'operations',
        label: t('palette.actions.operations', 'Opérations'),
        hint: '/operations',
        to: '/operations',
      });
    }
    if (canGenerateCards) {
      list.push({
        id: 'history',
        label: t('palette.actions.history', 'Historique'),
        hint: '/history',
        to: '/history',
      });
    }
    if (isAdmin) {
      list.push({
        id: 'admin-templates',
        label: t('palette.actions.adminTemplates', 'Admin · Templates'),
        hint: '/admin/templates',
        to: '/admin/templates',
      });
      list.push({
        id: 'admin-users',
        label: t('palette.actions.adminUsers', 'Admin · Utilisateurs'),
        hint: '/admin/users',
        to: '/admin/users',
      });
      list.push({
        id: 'admin-logs',
        label: t('palette.actions.adminLogs', 'Admin · Logs'),
        hint: '/admin/logs',
        to: '/admin/logs',
      });
      list.push({
        id: 'admin-dashboard',
        label: t('palette.actions.adminDashboard', 'Admin · Dashboard'),
        hint: '/admin/dashboard',
        to: '/admin/dashboard',
      });
    }
    return list;
  }, [canModerate, canBulk, canGenerateCards, isAdmin, t]);
}

function optionId(listId: string, index: number) {
  return `${listId}-${String(index)}`;
}

function PaletteOption({
  id,
  item,
  active,
  onPick,
  onHover,
}: {
  id: string;
  item: Item;
  active: boolean;
  onPick: () => void;
  onHover: () => void;
}) {
  const { t } = useTranslation();
  const title =
    item.kind === 'action'
      ? item.action.label
      : item.user.displayName || item.user.userPrincipalName;
  const subtitle =
    item.kind === 'action' ? item.action.hint : item.user.userPrincipalName;
  return (
    // An option, not a button: the keyboard drives the list from the combobox
    // field, and an option may not hold interactive content.
    <li
      id={id}
      role="option"
      aria-selected={active}
      onClick={onPick}
      onMouseEnter={onHover}
      className="cursor-pointer border-t border-t-rule first:border-t-0"
    >
      <div
        className="flex w-full items-center gap-3 px-4 py-2.5 text-left"
        style={{
          background: active ? 'var(--ink)' : 'transparent',
          color: active ? 'var(--inverse-fg)' : 'var(--ink)',
        }}
      >
        {item.kind === 'user' ? (
          <UserAvatar
            userId={item.user.id}
            displayName={item.user.displayName || item.user.userPrincipalName}
            size="sm"
          />
        ) : (
          <span
            className="mono w-12 shrink-0 text-center text-[10px]"
            style={{
              color: active ? 'rgba(242,244,248,0.55)' : 'var(--muted-soft)',
            }}
          >
            {t('palette.kindAction', 'ACT')}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{title}</p>
          <p
            className="mono mt-0.5 truncate text-xs"
            style={{
              color: active ? 'rgba(242,244,248,0.6)' : 'var(--muted)',
            }}
          >
            {subtitle}
          </p>
        </div>
        {active && (
          <span className="shrink-0 text-base text-green" aria-hidden>
            →
          </span>
        )}
      </div>
    </li>
  );
}
